import { useState } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Input,
  Modal,
} from '../ui';
import { useAuth } from '../../stores/auth.store';

interface UserProfileProps {
  onClose?: () => void;
}

type ProfileTab = 'overview' | 'settings' | 'security';

export function UserProfile({ onClose }: UserProfileProps) {
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<ProfileTab>('overview');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  if (!user) {
    return (
      <Card>
        <CardContent>
          <p className="text-slate-400">Please sign in to view your profile.</p>
        </CardContent>
      </Card>
    );
  }

  const tabs: { id: ProfileTab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'settings', label: 'Settings' },
    { id: 'security', label: 'Security' },
  ];

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-2xl font-bold text-white">
          {user.username.charAt(0).toUpperCase()}
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">{user.username}</h2>
          <p className="text-slate-400">{user.email}</p>
          {user.emailVerified && (
            <span className="inline-flex items-center gap-1 text-xs text-green-400">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Verified
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-purple-400 border-b-2 border-purple-400'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab user={user} />}
      {activeTab === 'settings' && <SettingsTab user={user} />}
      {activeTab === 'security' && (
        <SecurityTab
          onDeleteAccount={() => setIsDeleteModalOpen(true)}
          onSignOut={async () => {
            await signOut();
            onClose?.();
          }}
        />
      )}

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={async () => {
          // TODO: Implement account deletion
          await signOut();
          onClose?.();
        }}
      />
    </div>
  );
}

// Overview Tab
function OverviewTab({
  user,
}: {
  user: NonNullable<ReturnType<typeof useAuth>['user']>;
}) {
  const joinDate = new Date(user.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Mock stats for now
  const stats = {
    gamesPlayed: 42,
    totalScore: 125000,
    achievements: 7,
    rank: 'Gold',
  };

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Games Played" value={stats.gamesPlayed.toString()} />
        <StatCard
          label="Total Score"
          value={stats.totalScore.toLocaleString()}
        />
        <StatCard label="Achievements" value={stats.achievements.toString()} />
        <StatCard label="Rank" value={stats.rank} highlight />
      </div>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between">
            <span className="text-slate-400">Member since</span>
            <span className="text-white">{joinDate}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Subscription</span>
            <span className="text-purple-400 font-medium">Free Tier</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">User ID</span>
            <span className="text-slate-500 font-mono text-sm">{user.id}</span>
          </div>
        </CardContent>
      </Card>

      {/* Recent High Scores */}
      <Card>
        <CardHeader>
          <CardTitle>Recent High Scores</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-400 text-sm">
            No high scores yet. Start playing!
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="bg-slate-800/50 rounded-lg p-4 text-center">
      <p
        className={`text-2xl font-bold ${highlight ? 'text-purple-400' : 'text-white'}`}
      >
        {value}
      </p>
      <p className="text-xs text-slate-400 mt-1">{label}</p>
    </div>
  );
}

// Settings Tab
function SettingsTab({
  user,
}: {
  user: NonNullable<ReturnType<typeof useAuth>['user']>;
}) {
  const [username, setUsername] = useState(user.username);
  const [email, setEmail] = useState(user.email);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    // TODO: Implement profile update API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsSaving(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Edit Profile</CardTitle>
          <CardDescription>Update your account information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
          />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            helperText="Changing your email will require re-verification"
          />
        </CardContent>
        <CardFooter>
          <Button onClick={handleSave} isLoading={isSaving}>
            Save Changes
          </Button>
        </CardFooter>
      </Card>

      {/* Preferences */}
      <Card>
        <CardHeader>
          <CardTitle>Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center justify-between">
            <span className="text-slate-300">Email notifications</span>
            <input
              type="checkbox"
              defaultChecked
              className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-500 focus:ring-purple-500"
            />
          </label>
          <label className="flex items-center justify-between">
            <span className="text-slate-300">Show on leaderboards</span>
            <input
              type="checkbox"
              defaultChecked
              className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-500 focus:ring-purple-500"
            />
          </label>
        </CardContent>
      </Card>
    </div>
  );
}

// Security Tab
function SecurityTab({
  onDeleteAccount,
  onSignOut,
}: {
  onDeleteAccount: () => void;
  onSignOut: () => void;
}) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChanging, setIsChanging] = useState(false);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      return;
    }
    setIsChanging(true);
    // TODO: Implement password change API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setIsChanging(false);
  };

  return (
    <div className="space-y-6">
      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            fullWidth
          />
          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
          />
          <Input
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            state={
              confirmPassword && newPassword !== confirmPassword
                ? 'error'
                : 'default'
            }
          />
        </CardContent>
        <CardFooter>
          <Button onClick={handleChangePassword} isLoading={isChanging}>
            Update Password
          </Button>
        </CardFooter>
      </Card>

      {/* Session & Danger Zone */}
      <Card>
        <CardHeader>
          <CardTitle>Session</CardTitle>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={onSignOut} fullWidth>
            Sign Out
          </Button>
        </CardContent>
      </Card>

      <Card className="border-red-500/30">
        <CardHeader>
          <CardTitle className="text-red-400">Danger Zone</CardTitle>
          <CardDescription>
            Permanently delete your account and all associated data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="danger" onClick={onDeleteAccount}>
            Delete Account
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Delete Account Confirmation Modal
function DeleteAccountModal({
  isOpen,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') return;
    setIsDeleting(true);
    await onConfirm();
    setIsDeleting(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Account"
      description="This action cannot be undone"
      size="sm"
    >
      <div className="space-y-4">
        <p className="text-slate-300">
          Are you sure you want to delete your account? All your data, including
          high scores and achievements, will be permanently removed.
        </p>
        <Input
          label='Type "DELETE" to confirm'
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
          fullWidth
          placeholder="DELETE"
        />
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} fullWidth>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={confirmText !== 'DELETE'}
            isLoading={isDeleting}
            fullWidth
          >
            Delete Account
          </Button>
        </div>
      </div>
    </Modal>
  );
}
